import type { Vault } from '@/types/vault'
import { isVaultAboveMinScore } from '@/utils/vaultsList/isVaultAboveMinScore'
import { isVaultInCurators } from '@/utils/vaultsList/isVaultInCurators'
import { isVaultInNetworks } from '@/utils/vaultsList/isVaultInNetworks'
import { isVaultInProtocols } from '@/utils/vaultsList/isVaultInProtocols'
import { isVaultInTags } from '@/utils/vaultsList/isVaultInTags'
import { isVaultInTvlRange } from '@/utils/vaultsList/isVaultInTvlRange'
import { isVaultTokenInTokens } from '@/utils/vaultsList/isVaultTokenInTokens'
import { searchVaults } from '@/utils/vaultsList/searchVaults'
import { useDeferredValue, useMemo } from 'react'
import type { useVaultsSearchState } from './useVaultsSearchState'

type FilteredVaultsProps = {
  vaults: Vault[]
} & Pick<
  ReturnType<typeof useVaultsSearchState>,
  | 'searchFor'
  | 'selectedNetworks'
  | 'selectedTokens'
  | 'selectedProtocols'
  | 'selectedCurators'
  | 'selectedTags'
  | 'selectedTvlRange'
  | 'onlyTransactional'
  | 'minVaultScore'
>

export function useFilteredVaults({
  vaults,
  searchFor,
  selectedNetworks,
  selectedTokens,
  selectedProtocols,
  selectedCurators,
  selectedTags,
  selectedTvlRange,
  onlyTransactional,
  minVaultScore,
}: FilteredVaultsProps) {
  const deferredSearchFor = useDeferredValue(searchFor)

  const filteredVaults = useMemo(
    () =>
      vaults.filter(
        (vault) =>
          isVaultInNetworks(vault, selectedNetworks) &&
          isVaultInProtocols(vault, selectedProtocols) &&
          isVaultInCurators(vault, selectedCurators) &&
          isVaultInTags(vault, selectedTags) &&
          isVaultInTvlRange(vault, selectedTvlRange) &&
          isVaultTokenInTokens(vault, selectedTokens) &&
          (!onlyTransactional || vault.isTransactional) &&
          isVaultAboveMinScore(vault, minVaultScore)
      ),
    [
      vaults,
      selectedNetworks,
      selectedTokens,
      selectedProtocols,
      selectedCurators,
      selectedTags,
      selectedTvlRange,
      onlyTransactional,
      minVaultScore,
    ]
  )

  return useMemo(() => {
    if (!deferredSearchFor) return filteredVaults
    return searchVaults(filteredVaults, deferredSearchFor)
  }, [filteredVaults, deferredSearchFor])
}
